import { MinusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { getValidators, portalEn, Practice } from '@cc/schema';
import { Edit, RefreshButton, useForm } from '@refinedev/antd';

import { PlusCircleOutlined } from '@ant-design/icons';

import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';
import { useGetIdentity, useOne, useTranslate } from '@refinedev/core';
import { RuleObject } from 'antd/lib/form';
import { useWatch } from 'antd/lib/form/Form';
import countries from 'i18n-iso-countries';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'rooks';
import { getYupSyncValidator } from '../../helpers';
import { useFormSubmitDisabledAnyTouched } from '../../hooks';
import { IUser } from '../../providers';
import { urlRegExp } from '../initialSetup/step2';
import { InputNumber, message, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { RcFile, UploadFile } from 'antd/lib/upload/interface';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import useStyles from './styles.style';
import { resetCachedDbUser } from '../../providers/firebaseAuthNew';
import countriesEnJson from 'i18n-iso-countries/langs/en.json';
import countriesRuJson from 'i18n-iso-countries/langs/ru.json';

countries.registerLocale(countriesEnJson);
countries.registerLocale(countriesRuJson);

const { Paragraph, Title } = Typography;

export interface IProfessionalForm {
  firstName: string;
  lastName: string;
  wantsReferrals: boolean;
  approvedForReferrals: boolean;
  formats: string[];
  imageUrl: string;
  mentalHealthRole: string;
  title?: string;
  licenses: {
    state: string;
    number: string;
  }[];
  acceptingClients: 'yes' | 'no' | 'waitlist';
  yearsInPractice: number;
  about: string;
  education: string[];
  languages: string[];
  approaches: string[];
  costPerSessionFrom: number;
  costPerSessionTo: number;
  currency: string;
  slidingScale: boolean;
  modalities: string[];
  clientAges: string[];
  clientIssues: string[];
  linkToOtherProfile?: string;
  phone: string;
  extension?: string;
  freeInitialConsulation: boolean;
}

const initialLicense = {
  state: '',
  number: '',
};

const metaData = {
  fields: [
    'imageUrl',
    'wantsReferrals',
    'approvedForReferrals',
    'formats',
    'mentalHealthRole',
    'title',
    {
      licenses: ['state', 'number'],
    },
    'acceptingClients',
    'yearsInPractice',
    'about',
    'education',
    'languages',
    'approaches',
    'costPerSessionFrom',
    'costPerSessionTo',
    'currency',
    'slidingScale',
    'modalities',
    'clientAges',
    'clientIssues',
    'linkToOtherProfile',
    'phone',
    'extension',
    'freeInitialConsulation',
  ],
};

export const ProfileEdit = () => {
  const translate = useTranslate();

  const { i18n } = useTranslation();

  const { styles } = useStyles();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
    isLoading,
  } = useGetIdentity<IUser>();

  const { innerWidth } = useWindowSize();

  const textAlign = (innerWidth || 1000) < 995 ? 'center' : 'left';

  const formLayout = (innerWidth || 1000) < 995 ? 'vertical' : 'horizontal';

  const twoColumnLayoutCol1 =
    formLayout === 'horizontal'
      ? {
          xs: { span: 12, offset: 0 },
          sm: { span: 12, offset: 0 },
          md: { span: 12, offset: 0 },
          lg: { span: 12, offset: 0 },
          xl: { span: 12, offset: 0 },
          xxl: { span: 12, offset: 0 },
        }
      : { span: 24, offset: 0 };

  const twoColumnLayoutCol2 =
    formLayout === 'horizontal'
      ? {
          xs: { span: 12, offset: 0 },
          sm: { span: 12, offset: 0 },
          md: { span: 12, offset: 0 },
          lg: { span: 12, offset: 0 },
          xl: { span: 12, offset: 0 },
          xxl: { span: 12, offset: 0 },
        }
      : { span: 24, offset: 0 };
  const { profileValidator } = useMemo(
    () => getValidators(translate),
    [i18n.languages[0]],
  );

  const validationRules = getYupSyncValidator(
    translate,
    profileValidator,
  ) as RuleObject;

  /**
   * THE TWO NAMES ARE NOT ON THIS PAGE'S RECORD, and that is what made them vanish.
   *
   * Every other control here is a `therapistProfiles` field, which refine owns: `useForm` fetches
   * the record and calls `form.resetFields()` the moment it lands. The names are not in it — they
   * are on the `users` row, arriving separately through `useGetIdentity` — so the page used to put
   * `identity?.db?.firstName || ''` straight into `initialValues`, which is what `resetFields()`
   * writes into the fields.
   *
   * TWO INDEPENDENT REQUESTS, AND ONE RESET. If the record lands before the identity does, the
   * reset writes `''` into both name fields AND NOTHING EVER RESETS AGAIN — the record does not
   * change a second time, so refine's effect never re-runs and the therapist is left looking at an
   * empty First Name and Last Name over their own saved profile. On a local stack both answer in
   * milliseconds and the ordering is luck; in production the identity query fans out to Stripe
   * (`practiceUser { … practice { subscription { subscriptions { plan } } } }`) and loses the race
   * routinely. Founder, 2026-09-14, with the screenshot.
   *
   * The effect that used to stand here tried to notice the divergence and refetch the identity, and
   * could not: its guard required BOTH the form value and `identity.db.firstName` to be non-empty,
   * so it was dead in exactly the case that matters (one of them is empty), and after a save
   * refine's own `resetFields()` runs FIRST — `useForm` registers its effect before this component
   * declares any — which makes the two sides agree and the guard false.
   *
   * So the names get a small piece of state of their own, which is the LAST KNOWN-GOOD pair: the
   * identity fills it whenever it actually carries a name, a successful save fills it from what was
   * submitted, and nothing ever writes an empty string over a name that is already there. The
   * effect below re-applies it after every reset, and only to a field the therapist is not typing
   * into.
   */
  const [names, setNames] = useState<{ firstName: string; lastName: string }>({
    firstName: '',
    lastName: '',
  });

  const { formProps, saveButtonProps, queryResult } =
    useForm<IProfessionalForm>({
      warnWhenUnsavedChanges: true,
      resource: 'profile',
      action: 'edit',
      id: '-1',
      meta: metaData,
      redirect: false,
      /**
       * The save is the authority on the names from here on — they are what the therapist just
       * typed, and they are known before any refetch answers. The cached identity is refreshed
       * too (`resetCachedDbUser` clears the module-level memo `getIdentity` reads), so a later
       * mount of any page sees the new name, but the fields no longer WAIT on that.
       */
      onMutationSuccess: (_data, variables) => {
        const submitted = variables as Partial<IProfessionalForm> | undefined;
        setNames((prev) => ({
          firstName: submitted?.firstName || prev.firstName,
          lastName: submitted?.lastName || prev.lastName,
        }));
        resetCachedDbUser();
        refetchIdentity();
      },
    });

  useEffect(() => {
    setNames((prev) => ({
      firstName: identity?.db?.firstName || prev.firstName,
      lastName: identity?.db?.lastName || prev.lastName,
    }));
  }, [identity?.db?.firstName, identity?.db?.lastName]);

  useEffect(() => {
    const form = formProps.form;
    if (!form) {
      return;
    }

    (['firstName', 'lastName'] as const).forEach((field) => {
      const known = names[field];
      // never write an empty name, and never overwrite a name being typed
      if (!known || form.isFieldTouched(field)) {
        return;
      }
      if (form.getFieldValue(field) !== known) {
        form.setFieldValue(field, known);
      }
    });
    // `queryResult?.data?.data` is refine's own `resetFields()` trigger, so this runs right after
    // it — a component's effects run after the ones its hooks registered
  }, [names.firstName, names.lastName, queryResult?.data?.data]);

  const { handleFormChange, isDisabled: isSubmitDisabled } =
    useFormSubmitDisabledAnyTouched(formProps.form!);

  const { data: practiceData, refetch: refetchPractice } = useOne<Practice>({
    resource: 'practice',
    id: identity?.db?.practiceId || '',
    queryOptions: {
      enabled: false,
    },
    meta: {
      fields: [
        '_id',
        'practicePhone',
        'extension',
        {
          practiceLocations: [
            {
              data: ['_id', 'practiceCountry'],
            },
          ],
        },
      ],
    },
  });

  const costTo: number = useWatch('costPerSessionTo', formProps.form!) || 0;

  const isUs =
    practiceData?.data?.practiceLocations?.data?.[0]?.practiceCountry === 'US';
  const initialLicenses = isUs ? [initialLicense] : [];

  useEffect(() => {
    const getPracticeLocationData = async () => {
      if (identity?.db?.practiceId) {
        const res = await refetchPractice();
        if (
          res?.data?.data?.practiceLocations?.data?.[0]?.practiceCountry ===
          'US'
        ) {
          formProps.form!.setFieldValue(
            'licenses',
            formProps?.initialValues?.licenses?.length
              ? formProps?.initialValues?.licenses
              : [initialLicense],
          );
        }
      }
    };
    getPracticeLocationData();
  }, [identity?.db?.practiceId]);

  const imagePrefix = `providers/photos/profile/${identity?.fb?.uid || ''}/${(
    identity?.db?._id || ''
  ).replace('users/', '')}-img`;

  const [files, setFiles] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (formProps?.initialValues?.imageUrl) {
      setFiles([
        {
          uid: '-1',
          name: 'photo.png',
          status: 'done',
          url: formProps.initialValues.imageUrl,
        },
      ]);
    }
  }, [formProps?.initialValues?.imageUrl]);

  return (
    <div>
      <Edit
        title={translate('profile.profile')}
        saveButtonProps={{ ...saveButtonProps, disabled: isSubmitDisabled }}
        resource="profile"
        recordItemId={'-1'}
        headerButtons={
          <RefreshButton
            {...(isLoading ? { disabled: true } : {})}
            resource={'profile'}
            recordItemId={'-1'}
            meta={metaData}
          />
        }
      >
        <Paragraph>{translate('profile.description')}</Paragraph>
        <Form
          {...formProps}
          data-testid="profile-form"
          onFieldsChange={handleFormChange}
          labelWrap={formLayout === 'horizontal'}
          layout={formLayout}
          size="large"
          scrollToFirstError
          initialValues={
            formProps.initialValues
              ? {
                  ...formProps.initialValues,
                  firstName: names.firstName,
                  lastName: names.lastName,
                }
              : {
                  firstName: names.firstName,
                  lastName: names.lastName,
                  imageUrl: '',
                  wantsReferrals: false,
                  approvedForReferrals: false,
                  formats: [],
                  mentalHealthRole: undefined,
                  title: '',
                  licenses: initialLicenses,
                  acceptingClients: 'yes',
                  yearsInPractice: undefined,
                  about: '',
                  education: [''],
                  languages: [],
                  approaches: [],
                  costPerSessionFrom: undefined,
                  costPerSessionTo: undefined,
                  currency: 'USD',
                  slidingScale: false,
                  modalities: [],
                  clientAges: [],
                  clientIssues: [],
                  linkToOtherProfile: '',
                  phone: '',
                  extension: '',
                  freeInitialConsulation: false,
                }
          }
        >
          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col {...twoColumnLayoutCol1}>
              <Form.Item
                name="wantsReferrals"
                label={translate('profile.wantsReferrals')}
                rules={[validationRules]}
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col {...twoColumnLayoutCol2}>
              <Form.Item
                name="approvedForReferrals"
                label={translate('profile.approvedForReferrals')}
                valuePropName="checked"
                tooltip={translate('profile.approvedForReferralsTip')}
              >
                <Checkbox disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col {...twoColumnLayoutCol1}>
              <Form.Item
                name="acceptingClients"
                label={translate('profile.acceptingClients')}
                required
                rules={[validationRules]}
              >
                <Select>
                  {portalEn.profile.acceptingOptions.map((i: string, idx) => (
                    <Select.Option key={i} value={i}>
                      {
                        translate(`profile.acceptingOptions`, {
                          returnObjects: true,
                        })[idx]
                      }
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col {...twoColumnLayoutCol2}>
              <Form.Item
                name="formats"
                label={translate('profile.formats')}
                required
                rules={[validationRules]}
              >
                <Select mode="tags">
                  {portalEn.profile.formatsOptions.map((i: string, idx) => (
                    <Select.Option key={i} value={i}>
                      {
                        translate(`profile.formatsOptions`, {
                          returnObjects: true,
                        })[idx]
                      }
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col {...twoColumnLayoutCol1}>
              <Form.Item
                name="firstName"
                label={translate('profile.firstName')}
                required
                rules={[validationRules]}
              >
                <Input
                  type="text"
                  placeholder={translate('profile.firstName')}
                />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={translate('profile.lastName')}
                required
                rules={[validationRules]}
              >
                <Input
                  type="text"
                  placeholder={translate('profile.lastName')}
                />
              </Form.Item>

              <Form.Item
                name="title"
                label={translate('profile.professionalTitle')}
                rules={[validationRules]}
              >
                <Input
                  type="text"
                  placeholder={translate(
                    'profile.professionalTitlePlaceholder',
                  )}
                />
              </Form.Item>

              <Form.Item
                name="mentalHealthRole"
                label={translate('profile.mentalHealthRole')}
                required
                rules={[validationRules]}
              >
                <Select placeholder={translate(`profile.selectOrType`)}>
                  {portalEn.profile.mentalHealthRoles.map((i, idx) => (
                    <Select.Option key={i} value={i}>
                      {
                        translate(`profile.mentalHealthRoles`, {
                          returnObjects: true,
                        })[idx]
                      }
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col {...twoColumnLayoutCol2}>
              <Form.Item name="imageUrl" label={translate('profile.imageUrl')}>
                <ImgCrop rotationSlider cropShape="rect" zoomSlider>
                  <Upload
                    accept="image/*"
                    className={styles['profile-upload-card']}
                    beforeUpload={(file) => {
                      const isImage = file.type.indexOf('image/') === 0;
                      if (!isImage) {
                        message.error(translate('profile.onlyImage'));
                      }

                      // You can remove this validation if you want
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        message.error(translate('profile.sizeLimit'));
                      }
                      return isImage && isLt5M;
                    }}
                    customRequest={({ onError, onSuccess, file }) => {
                      const upload = async () => {
                        const storage = getStorage();
                        const imageRef = ref(storage, imagePrefix);
                        try {
                          const image = await uploadBytes(
                            imageRef,
                            file as RcFile,
                          );
                          // @ts-ignore
                          onSuccess(null, image);

                          const imageUrl = await getDownloadURL(imageRef);

                          formProps.form!.setFields([
                            {
                              name: 'imageUrl',
                              touched: true,
                              value: imageUrl,
                            },
                          ]);

                          handleFormChange();

                          message.success(translate('profile.uploadSuccess'));
                        } catch (e) {
                          message.error(String(e));
                        }
                      };
                      upload();
                    }}
                    listType="picture-card"
                    fileList={files}
                    onChange={({ file, fileList }) => {
                      setFiles(fileList);
                    }}
                    onRemove={async (file) => {
                      const index = files.indexOf(file);
                      const newFileList = files.slice();
                      newFileList.splice(index, 1);

                      try {
                        setFiles(newFileList);

                        formProps.form!.setFields([
                          {
                            name: 'imageUrl',
                            touched: true,
                            value: '',
                          },
                        ]);

                        handleFormChange();

                        const storage = getStorage();
                        const imageRef = ref(storage, imagePrefix);

                        const res = await deleteObject(imageRef);
                      } catch (e) {
                        message.error(String(e));
                      }
                    }}
                    onPreview={async (file: UploadFile) => {
                      let src = file.url as string;
                      if (!src) {
                        src = await new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.readAsDataURL(file.originFileObj as RcFile);
                          reader.onload = () =>
                            resolve(reader.result as string);
                        });
                      }
                      const image = new Image();
                      image.src = src;
                      const imgWindow = window.open(src);
                      imgWindow?.document.write(image.outerHTML);
                    }}
                    multiple={false}
                    maxCount={1}
                  >
                    {files.length < 1 ? (
                      <>
                        <UploadOutlined
                          className={styles['profile-upload-card__upload-icon']}
                        />{' '}
                        {translate('profile.uploadPhoto')}
                      </>
                    ) : null}
                  </Upload>
                </ImgCrop>
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col {...twoColumnLayoutCol1}>
              <Form.Item
                name="phone"
                label={translate('profile.phone')}
                required
                rules={[validationRules]}
              >
                <Input
                  type="tel"
                  size="large"
                  placeholder={translate('profile.phone')}
                />
              </Form.Item>
            </Col>

            <Col {...twoColumnLayoutCol2}>
              <Form.Item
                name="extension"
                label={translate('profile.extension')}
                rules={[validationRules]}
              >
                <Input
                  size="large"
                  placeholder={translate('profile.extension')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {isUs ? (
            <Form.List name="licenses">
              {(fields, { add, remove }, errors) => {
                return (
                  <>
                    {fields.map((field, index) => {
                      return (
                        <div key={index}>
                          <Row
                            gutter={8}
                            style={{
                              flexDirection:
                                formLayout === 'horizontal' ? 'row' : 'column',
                            }}
                          >
                            <Col {...twoColumnLayoutCol1}>
                              <Form.Item
                                {...field}
                                name={[`${field.name}`, 'state']}
                                label={translate('profile.licensedInStates')}
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <Select>
                                  {portalEn.states.map((o, idx) => (
                                    <Select.Option value={o} key={o}>
                                      {
                                        translate('states', {
                                          returnObjects: true,
                                        })[idx]
                                      }
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>

                            <Col
                              span={
                                formLayout === 'horizontal'
                                  ? index > 0
                                    ? 11
                                    : 12
                                  : 24
                              }
                            >
                              <Form.Item
                                {...field}
                                name={[`${field.name}`, 'number']}
                                label={translate('profile.licenseNumber')}
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <Input
                                  type="text"
                                  placeholder={translate(
                                    'profile.licensedInStates',
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            {index > 0 ? (
                              <Col span={1}>
                                <MinusCircleOutlined
                                  style={{ margin: '5px 0 0 10px' }}
                                  onClick={() => remove(field.name)}
                                />
                              </Col>
                            ) : null}
                          </Row>
                        </div>
                      );
                    })}

                    <Form.Item wrapperCol={{ offset: 1 }}>
                      <Button
                        type="primary"
                        onClick={() => add({ state: '', number: '' })}
                        shape="round"
                        icon={<PlusCircleOutlined />}
                      >
                        {translate('profile.addState')}
                      </Button>
                    </Form.Item>
                  </>
                );
              }}
            </Form.List>
          ) : null}

          <Form.Item
            name="yearsInPractice"
            label={translate('profile.yearsInPractice')}
            required
            rules={[validationRules]}
          >
            <InputNumber
              min={0}
              placeholder={translate('profile.yearsInPractice')}
            />
          </Form.Item>

          <Form.Item
            name="about"
            label={translate('profile.about')}
            required
            rules={[validationRules]}
          >
            <Input.TextArea
              autoSize={{ minRows: 4, maxRows: 8 }}
              placeholder={translate('profile.about')}
            />
          </Form.Item>

          <Form.List name="education">
            {(fields, { add, remove }, errors) => {
              return (
                <>
                  {fields.map((field, index) => {
                    return (
                      <Row
                        style={{
                          flexDirection:
                            formLayout === 'horizontal' ? 'row' : 'column',
                        }}
                        key={index}
                      >
                        <Col
                          span={
                            formLayout === 'horizontal'
                              ? index > 0
                                ? 11
                                : 12
                              : 24
                          }
                        >
                          <Form.Item
                            {...field}
                            label={translate('profile.education')}
                            required
                            rules={[
                              {
                                required: true,
                              },
                            ]}
                          >
                            <Input
                              type="text"
                              placeholder={translate('profile.education')}
                            />
                          </Form.Item>
                        </Col>

                        {index > 0 ? (
                          <Col span={1}>
                            <MinusCircleOutlined
                              style={{ margin: '5px 0 0 10px' }}
                              onClick={() => remove(field.name)}
                            />
                          </Col>
                        ) : null}
                      </Row>
                    );
                  })}

                  <Form.Item wrapperCol={{ offset: 1 }}>
                    <Button
                      type="primary"
                      onClick={() => add('')}
                      shape="round"
                      icon={<PlusCircleOutlined />}
                      style={{ marginTop: '15px' }}
                    >
                      {translate('profile.addEducation')}
                    </Button>
                  </Form.Item>
                </>
              );
            }}
          </Form.List>

          <Form.Item
            name="languages"
            label={translate('profile.languages')}
            required
            rules={[validationRules]}
          >
            <Select mode="tags" placeholder={translate('profile.langaugeHint')}>
              {portalEn.profile.languagesList.map((i: string, idx) => (
                <Select.Option key={i} value={i}>
                  {
                    translate(`profile.languagesList`, {
                      returnObjects: true,
                    })[idx]
                  }
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="approaches"
            label={translate('profile.approachLabel')}
            required
            rules={[validationRules]}
          >
            <Select mode="tags" placeholder={translate('profile.selectOrType')}>
              {portalEn.profile.approaches.map((i: string, idx) => (
                <Select.Option key={i} value={i}>
                  {
                    translate(`profile.approaches`, {
                      returnObjects: true,
                    })[idx]
                  }
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />
          <Title level={5} style={{ textAlign }}>
            {translate('profile.finances')}
          </Title>

          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col span={16}>
              <Form.Item
                name="costPerSessionFrom"
                label={translate('profile.costPerSessionFrom')}
                rules={[validationRules]}
                required
              >
                <InputNumber
                  min={0}
                  placeholder={translate('profile.fromPlaceholder')}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="costPerSessionTo"
                label={translate('profile.costPerSessionTo')}
                required
                rules={[validationRules]}
              >
                <InputNumber
                  min={costTo}
                  placeholder={translate('profile.costPerSessionTo')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currency"
                label={translate('profile.currency')}
                rules={[validationRules]}
                required
              >
                <Input type="string" />
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col {...twoColumnLayoutCol1}>
              <Form.Item
                name="freeInitialConsulation"
                label={translate('profile.freeInitialConsultation')}
                rules={[validationRules]}
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col {...twoColumnLayoutCol2}>
              <Form.Item
                name="slidingScale"
                tooltip={translate('profile.slidingScaleTip')}
                label={translate('profile.slidingScale')}
                rules={[validationRules]}
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Title level={5} style={{ textAlign }}>
            {translate('profile.clients')}
          </Title>

          <Row
            gutter={8}
            style={{
              flexDirection: formLayout === 'horizontal' ? 'row' : 'column',
            }}
          >
            <Col span={8} {...twoColumnLayoutCol1}>
              <Form.Item
                name="modalities"
                label={translate('profile.modality')}
                required
                rules={[validationRules]}
              >
                <Select mode="multiple">
                  {portalEn.profile.modalities.map((i: string, idx) => (
                    <Select.Option key={i} value={i}>
                      {
                        translate(`profile.modalities`, {
                          returnObjects: true,
                        })[idx]
                      }
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={16} {...twoColumnLayoutCol2}>
              <Form.Item
                name="clientAges"
                label={translate('profile.age')}
                required
                rules={[validationRules]}
              >
                <Select mode="multiple">
                  {portalEn.profile.clientAges.map((i: string, idx) => (
                    <Select.Option key={i} value={i}>
                      {
                        translate(`profile.clientAges`, {
                          returnObjects: true,
                        })[idx]
                      }
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="clientIssues"
            label={translate('profile.issues')}
            required
            rules={[validationRules]}
          >
            <Select mode="tags" placeholder={translate('profile.selectOrType')}>
              {portalEn.profile.clientIssues.map((i: string, idx) => (
                <Select.Option key={i} value={i}>
                  {
                    translate(`profile.clientIssues`, {
                      returnObjects: true,
                    })[idx]
                  }
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />
          <Title level={5} style={{ textAlign }}>
            {translate('profile.otherInfo')}
          </Title>

          <Form.Item
            name="linkToOtherProfile"
            label={translate('profile.otherProfileLink')}
            rules={[
              {
                pattern: urlRegExp,
                message: translate('validators.url'),
              },
            ]}
          >
            <Input
              type="url"
              size="large"
              addonBefore="https://"
              placeholder={translate('profile.otherProfileLink')}
            />
          </Form.Item>
        </Form>
      </Edit>
    </div>
  );
};
